// Shared list-response + request-param contract helpers for the v2
// dashboard API clients.
//
// CHG-2916 / ITER-905655 — the v2 list clients (donors / donations /
// designations / recurring-donations / pledges / messages) were authored
// against a `{ items, total }` wire shape and an `offset / limit / q /
// sort=<field>:<dir>` request contract that NO live backend list endpoint
// actually speaks. The live FastAPI routers return FOUR distinct paged
// envelopes and read 1-based `page` / `per_page` / `search`:
//
//   A. `{ data, pagination: { page, per_page, total, total_pages } }`
//      — donors, donations, pledges (DonorPagedEnvelope etc.)
//   B. `{ data, meta: { page, per_page, total, pages } }`
//      — designations (DesignationPagedEnvelope)
//   C. `{ items, total, cursor? }`
//      — recurring-donations, messages (RecurringDonationPagedEnvelope,
//        MessageList)
//   D. FLAT `{ data, page, per_page, total, total_pages }`
//      — the donor-portal /my-giving/* paged surfaces
//        (PortalDonationPagedEnvelope + the CHG-2947 Slice F
//        param-presence-paged recurring/pledges/receipts lists and the
//        feed/org-updates envelopes). Added by CHG-2947: pre-fix the
//        normalizer fell through to `data.length` for (D), silently
//        capping every total at the page size.
//
// Pre-fix every v2 list page read `r.data.items` / `r.data.total`, which
// are `undefined` against the (A)/(B) envelopes → empty tables; and the
// donor list shipped `sort=last_gift_at:desc`, which the strict
// `DonorSortBy` enum (no `:desc` suffix) rejects with a 422 on first load.
//
// v1 (`web/dashboard/src/api.ts:469 normalizeListResponse`) already
// absorbs the (A)/(B) shapes into a uniform result; v2 simply never got
// it. This module is v2's equivalent, extended to also cover the
// `{ items, total, cursor }` (C) shape the recurring/messages routers use.

/** The four envelope shapes any live list endpoint may return. */
interface PaginationLike {
  page?: number;
  per_page?: number;
  total?: number;
  // `total_pages` (donor/donation/pledge) and `pages` (designation) are
  // the same concept under two names — accept either.
  total_pages?: number;
  pages?: number;
}

export interface RawListEnvelope<T> {
  // (A) `{ data, pagination }` / (B) `{ data, meta }`
  data?: T[];
  pagination?: PaginationLike;
  meta?: PaginationLike;
  // (C) `{ items, total, cursor }`
  items?: T[];
  // (C) top-level total — ALSO carried by (D), the donor-portal FLAT
  // envelope `{ data, page, per_page, total, total_pages }`
  // (CHG-2947 / Slice F: /my-giving/donations + the param-presence-paged
  // /my-giving/{recurring-donations,pledges,receipts,feed,org/{id}/updates}).
  total?: number;
  cursor?: string | null;
  // (D) remaining flat-envelope page fields (informational; the
  // normalizer only reads `total`).
  page?: number;
  per_page?: number;
  total_pages?: number;
}

/**
 * Uniform list result the v2 ListPage composition consumes.
 *
 * The generated screen queryFns return `{ items, total }` to the
 * ListPage/DataTable; keep that as the canonical FE shape so no
 * page/composition code changes — only the api-client boundary moves.
 */
export interface NormalizedList<T> {
  items: T[];
  total: number;
}

/**
 * Collapse any of the four backend list envelopes into `{ items, total }`.
 *
 * Resolution order:
 *   1. `data` present → (A)/(B)/(D). `total` from `pagination.total` /
 *      `meta.total`, then the (D) flat top-level `total` (CHG-2947 —
 *      pre-fix the flat donor-portal envelope mis-reported `total` as
 *      `data.length`, capping every paged portal list at one page),
 *      falling back to `data.length`.
 *   2. else `items` present → (C). `total` from `total`, falling back to
 *      `items.length`.
 *   3. else empty list (defensive — never throw on a malformed payload;
 *      an empty table is strictly better than a crashed page).
 */
export function normalizeListResponse<T>(
  payload: RawListEnvelope<T> | null | undefined,
): NormalizedList<T> {
  if (!payload || typeof payload !== "object") {
    return { items: [], total: 0 };
  }

  if (Array.isArray(payload.data)) {
    const data = payload.data;
    const meta = payload.pagination ?? payload.meta;
    // (D) flat-envelope branch (CHG-2947): top-level `total` when `data`
    // is present and no nested pagination/meta object carries one.
    const total =
      typeof meta?.total === "number"
        ? meta.total
        : typeof payload.total === "number"
          ? payload.total
          : data.length;
    return { items: data, total };
  }

  if (Array.isArray(payload.items)) {
    const items = payload.items;
    const total = typeof payload.total === "number" ? payload.total : items.length;
    return { items, total };
  }

  return { items: [], total: 0 };
}

/**
 * Shared 0-based-page → 1-based `page` / `per_page` / `search` request
 * params. The v2 ListPage passes `page` as a 0-based index (first page =
 * 0) with a `pageSize`; every live router uses 1-based `page` with
 * `Query(ge=1)` and `per_page` (`offset` / `limit` / `q` are NOT read).
 */
export function listPageParams(opts: {
  page?: number;
  pageSize?: number;
  search?: string;
}): { page: number; per_page: number; search: string | undefined } {
  const pageSize = opts.pageSize ?? 50;
  // ListPage's `page` is a 0-based index; backend `page` is 1-based.
  const page = (opts.page ?? 0) + 1;
  return {
    page,
    per_page: pageSize,
    // empty string would still match Query(default=None) loosely on some
    // routers; normalize "" → undefined so it's omitted entirely.
    search: opts.search ? opts.search : undefined,
  };
}

/**
 * Map the ListPage `(sortField, sortDirection)` pair onto the donor
 * router's strict `DonorSortBy` enum value.
 *
 * The backend enum (`src/api/app/schemas/donor.py:DonorSortBy`) encodes
 * each member's natural direction in the service layer, so it takes NO
 * `:desc` suffix and NO separate `direction` param — only the bare enum
 * string. Valid members:
 *   lifetime_giving_minor_units | last_gift_at | total_donations |
 *   name | created_at
 *
 * The donor list-table column `field`s (from donor.entity-hint.yaml) are
 * `full_name` / `lifetime_giving` / `last_gift_at`; map them to the enum.
 * Anything unrecognized (or absent) → undefined, so the router applies
 * its own default (`last_gift_at`) instead of 422-ing on a bad value.
 * This is what makes the FIRST donor-list load — which ships
 * `defaultSort: { field: "last_gift_at" }` from the generated config —
 * send a valid enum and stop 422-ing without hand-editing the generated
 * screen file.
 */
const DONOR_SORT_FIELD_MAP: Record<string, string> = {
  full_name: "name",
  name: "name",
  lifetime_giving: "lifetime_giving_minor_units",
  lifetime_giving_minor_units: "lifetime_giving_minor_units",
  last_gift_at: "last_gift_at",
  total_donations: "total_donations",
  created_at: "created_at",
};

export function donorSortParam(sortField?: string): string | undefined {
  if (!sortField) return undefined;
  return DONOR_SORT_FIELD_MAP[sortField];
}
